import React, { useState, useRef, useCallback } from 'react';
import { Camera, X, Loader2, Check, RotateCcw } from 'lucide-react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface CoverImageUploadProps {
    currentCoverUrl: string;
    onUpload: (base64DataUrl: string) => Promise<boolean>;
    isUploading?: boolean;
}

// Helper function to create centered crop with 3:1 aspect ratio for cover
function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 100,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    );
}

// Convert crop to base64 data URL
async function getCroppedImgAsBase64(
    image: HTMLImageElement,
    crop: PixelCrop
): Promise<string | null> {
    const canvas = document.createElement('canvas');

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Output size for cover (max 1600x600)
    const outputWidth = Math.min(1600, crop.width * scaleX);
    const outputHeight = Math.min(600, crop.height * scaleY);

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        outputWidth,
        outputHeight
    );

    return canvas.toDataURL('image/jpeg', 0.85);
}

const CoverImageUpload: React.FC<CoverImageUploadProps> = ({
    currentCoverUrl,
    onUpload,
    isUploading = false
}) => {
    const [showModal, setShowModal] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    // Cropping states
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

    const imgRef = useRef<HTMLImageElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateFile = (file: File): string | null => {
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const maxSize = 10 * 1024 * 1024; // 10MB

        if (!validTypes.includes(file.type)) {
            return '请上传 JPG、PNG、GIF 或 WebP 格式的图片';
        }

        if (file.size > maxSize) {
            return '图片大小不能超过 10MB';
        }

        return null;
    };

    const handleFileSelect = useCallback((file: File) => {
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }

        setError(null);

        const reader = new FileReader();
        reader.onloadend = () => {
            setImageSrc(reader.result as string);
            setCrop(undefined);
            setCompletedCrop(undefined);
        };
        reader.readAsDataURL(file);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
        e.target.value = '';
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;
        // 3:1 aspect ratio for cover image
        setCrop(centerAspectCrop(width, height, 3));
    }, []);

    const handleConfirmUpload = async () => {
        if (!imgRef.current || !completedCrop) {
            setError('请先选择裁剪区域');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const base64DataUrl = await getCroppedImgAsBase64(
                imgRef.current,
                completedCrop
            );

            if (!base64DataUrl) {
                setError('图片处理失败，请重试');
                return;
            }

            const success = await onUpload(base64DataUrl);
            if (success) {
                setShowModal(false);
                setImageSrc(null);
            } else {
                setError('上传失败，请重试');
            }
        } catch (err) {
            setError('上传失败，请重试');
        } finally {
            setUploading(false);
        }
    };

    const handleCancel = () => {
        setImageSrc(null);
        setCrop(undefined);
        setCompletedCrop(undefined);
        setError(null);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        handleCancel();
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleReselect = () => {
        setImageSrc(null);
        setCrop(undefined);
        setCompletedCrop(undefined);
        openFileDialog();
    };

    return (
        <>
            {/* Edit Button - appears on hover */}
            <button
                type="button"
                onClick={() => setShowModal(true)}
                className="absolute bottom-4 right-4 px-4 py-2 bg-black/60 backdrop-blur-sm text-white rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-all hover:bg-black/80 flex items-center gap-2 z-10"
            >
                <Camera className="w-4 h-4" />
                编辑封面
            </button>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleInputChange}
                className="hidden"
            />

            {/* Upload & Crop Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={handleCloseModal}
                    />

                    {/* Modal Content */}
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
                        {/* Close Button */}
                        <button
                            onClick={handleCloseModal}
                            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>

                        <h3 className="text-xl font-bold text-gray-900 mb-6">更换封面图片</h3>

                        {/* Error message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {!imageSrc ? (
                            /* Drop Zone */
                            <div
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={openFileDialog}
                                className={`
                                    border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all
                                    ${dragActive
                                        ? 'border-black bg-gray-50'
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }
                                `}
                            >
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                                    <Camera className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-700 font-medium mb-2">
                                    拖拽图片到这里，或点击选择
                                </p>
                                <p className="text-sm text-gray-500">
                                    推荐尺寸 1600×600，支持 JPG、PNG、GIF、WebP，最大 10MB
                                </p>
                            </div>
                        ) : (
                            /* Cropper */
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-2">
                                    拖动调整裁剪区域，选择要展示的封面区域
                                </p>

                                {/* Crop Area */}
                                <div className="flex justify-center bg-gray-100 rounded-xl p-4 overflow-hidden">
                                    <ReactCrop
                                        crop={crop}
                                        onChange={(c) => setCrop(c)}
                                        onComplete={(c) => setCompletedCrop(c)}
                                        aspect={3}
                                        className="max-h-[400px]"
                                    >
                                        <img
                                            ref={imgRef}
                                            src={imageSrc}
                                            alt="待裁剪图片"
                                            onLoad={onImageLoad}
                                            style={{ maxHeight: '400px', maxWidth: '100%' }}
                                        />
                                    </ReactCrop>
                                </div>

                                {/* Preview */}
                                {completedCrop && (
                                    <div className="p-4 bg-gray-50 rounded-xl">
                                        <p className="text-xs text-gray-500 mb-2">预览效果</p>
                                        <div
                                            className="w-full h-20 rounded-lg overflow-hidden border border-gray-200"
                                            style={{
                                                backgroundImage: `url(${imageSrc})`,
                                                backgroundSize: `${(imgRef.current?.width || 0) / (completedCrop.width / 100)}%`,
                                                backgroundPosition: `-${(completedCrop.x / (completedCrop.width / 100))}px -${(completedCrop.y / (completedCrop.width / 100) * (completedCrop.height / completedCrop.width))}px`
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Action buttons */}
                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={handleReselect}
                                        className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                        重新选择
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirmUpload}
                                        disabled={uploading || !completedCrop}
                                        className="flex-1 px-4 py-3 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {uploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                上传中...
                                            </>
                                        ) : (
                                            <>
                                                <Check className="w-4 h-4" />
                                                确认更换
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Current Cover Preview */}
                        {!imageSrc && currentCoverUrl && (
                            <div className="mt-6 pt-6 border-t border-gray-100">
                                <p className="text-sm text-gray-500 mb-3">当前封面</p>
                                <div className="w-full h-24 rounded-xl overflow-hidden">
                                    <img
                                        src={currentCoverUrl}
                                        alt="当前封面"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default CoverImageUpload;
